// Presence (17-people.md, "Presence"): others from their latest presence event, live; oneself from
// the status shown (34). One listener on the client serves every picture.
import { ClientEvent, EventType, type MatrixClient, type MatrixEvent } from 'matrix-js-sdk';
import { useEffect, useState, type ReactNode } from 'react';
import { Avatar } from '../blocks/Bits';
import { t, type Key } from '../i18n';
import { thumbnail } from '../session/media';
import { onStatus, shownStatus } from '../status/status';

export type Presence = 'online' | 'idle' | 'dnd' | 'offline';
const DND = 'Do Not Disturb';
const latest = new Map<string, { presence?: string; status_msg?: string }>();
const listeners = new Set<() => void>();
const watched = new WeakSet<MatrixClient>();

function watch(client: MatrixClient) {
  if (watched.has(client)) return;
  watched.add(client);
  const changed = () => listeners.forEach((l) => l());
  client.on(ClientEvent.Event, (ev: MatrixEvent) => {
    if (ev.getType() !== EventType.Presence) return;
    latest.set(ev.getSender() ?? '', ev.getContent());
    changed();
  });
  onStatus(changed);
}

/** Draws again on every presence change. */
export function usePresence(client: MatrixClient) {
  const [, set] = useState(0);
  useEffect(() => {
    watch(client);
    const l = () => set((n) => n + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, [client]);
}

/** A person's presence and status text. */
export function presenceOf(client: MatrixClient, userId: string): { presence: Presence; text?: string; invisible?: boolean } {
  if (userId === client.getUserId()) {
    const status = shownStatus(client);
    return status === 'invisible' ? { presence: 'offline', invisible: true } : { presence: status };
  }
  const user = client.getUser(userId);
  const last = latest.get(userId) ?? { presence: user?.presence, status_msg: user?.presenceStatusMsg };
  const text = last.status_msg || undefined;
  if (last.presence === 'online') return { presence: 'online', text };
  if (last.presence === 'unavailable') return text === DND ? { presence: 'dnd' } : { presence: 'idle', text };
  return { presence: 'offline' };
}

export const PRESENCE_WORDS: Record<Presence, Key> = { online: 'people.online', idle: 'people.idle', dnd: 'people.dnd', offline: 'people.offline' };
export const isOnline = (p: Presence) => p !== 'offline';

/** A round picture with its presence dot (17, member list and card). */
export function PersonPicture({ client, userId, name, mxc, size, picture }: { client: MatrixClient; userId: string; name: string; mxc?: string | null; size: number; picture?: ReactNode }) {
  const { presence, text, invisible } = presenceOf(client, userId);
  const words = `${t(PRESENCE_WORDS[presence])}${text ? ` • ${text}` : ''}`;
  return (
    <span className={`person-picture ${presence}`} data-desyr-status={invisible ? 'invisible' : presence}>
      {picture ?? <Avatar id={userId} name={name} src={thumbnail(client, mxc, size)} size={size} />}
      <span className="presence-dot" role="img" aria-label={words} title={words} />
    </span>
  );
}
